import createClient from 'openapi-fetch';

import { API_URL } from '@/constants/env';
import { userManager } from '@/constants/oidc';
import { type paths } from '@/libs/openapi/schema';
import { queryClient } from '@/main';

export class HttpException extends Error {
  response?: Response;
  isGlobal: boolean;

  constructor(
    {
      message,
      response,
      isGlobal,
    }: {
      message?: string;
      response?: Response;
      /** Wether the error should be handled by global error handler */
      isGlobal?: boolean;
    } = { message: 'Something went wrong' },
  ) {
    super(message);
    this.name = 'HttpException';
    this.response = response;
    this.isGlobal = isGlobal ?? true;
  }
}

export const client = createClient<paths>({
  baseUrl: API_URL,
  fetch: async (...args) => {
    try {
      return await fetch(...args);
    } catch {
      throw new HttpException({ message: 'Network error. Please check your connection.', isGlobal: true });
    }
  },
});

let refreshTokenPromise: Promise<string | null> | null = null;

const getAccessToken = async (): Promise<string | null> => {
  const user = await userManager.getUser();
  if (!user) {
    return null;
  }

  if (!user.expired) {
    return user.access_token;
  }

  try {
    const refreshedUser = await userManager.signinSilent();
    return refreshedUser?.access_token ?? null;
  } catch {
    await userManager.removeUser();
    return null;
  }
};

const refreshAccessToken = async (): Promise<string | null> => {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = (async () => {
    try {
      const user = await userManager.getUser();
      if (!user) {
        return null;
      }

      try {
        const refreshedUser = await userManager.signinSilent();
        return refreshedUser?.access_token ?? null;
      } catch {
        await userManager.removeUser();
        return null;
      }
    } finally {
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
};

client.use({
  async onRequest({ request }) {
    const token = await getAccessToken();
    if (token) {
      request.headers.set('Authorization', `Bearer ${token}`);
    }
    return request;
  },
  async onResponse({ request, response }) {
    if (!response.ok && response.status === 401) {
      const refreshedToken = await refreshAccessToken();
      if (!refreshedToken) {
        queryClient.clear();
        await userManager.removeUser();
        await userManager.signinRedirect();
        return response;
      }

      const retryRequest = new Request(request);
      retryRequest.headers.set('Authorization', `Bearer ${refreshedToken}`);
      const retry = await fetch(retryRequest);
      if (retry.ok) {
        return retry;
      }

      response = retry;
    }

    if (!response.ok && response.status === 403) {
      let body: { message?: string } = {};
      try {
        body = await response.clone().json();
      } catch {
        // non-JSON body, ignore
      }

      if (body.message === 'jwt expired') {
        const refreshedToken = await refreshAccessToken();
        if (!refreshedToken) {
          queryClient.clear();
          await userManager.removeUser();
          await userManager.signinRedirect();
          return response;
        }

        const retryRequest = new Request(request);
        retryRequest.headers.set('Authorization', `Bearer ${refreshedToken}`);
        const retry = await fetch(retryRequest);
        if (retry.ok) {
          return retry;
        }

        response = retry;
      }
    }

    return response;
  },
});

// eslint-disable-next-line no-restricted-syntax
export * from './schema';
